import xss from "xss";
import type { IWhiteList, IFilterXSSOptions } from "xss";

// `xss` is the `filterXSS` function; its static helpers (getDefaultWhiteList,
// escapeAttrValue) live on that function object. Reach them via a typed view
// of the default export — named ESM imports of this CommonJS module resolve to
// `undefined` under Vite's SSR interop.
const { getDefaultWhiteList, escapeAttrValue } = xss as unknown as {
	getDefaultWhiteList: () => IWhiteList;
	escapeAttrValue: (value: string) => string;
};

// Presentational / semantic attributes safe to allow on any tag — they carry
// no scripting surface. `style` values are still sanitized by xss's CSS filter
// (it strips `expression()`, `url(javascript:…)`, etc.).
const GLOBAL_ATTRS = ["class", "id", "style", "title", "dir", "lang", "role"];

// Safe semantic/structural tags the default whitelist omits but CMS rich-text
// may emit. (Scripting/embedding tags — script, iframe, object, embed — are
// intentionally NOT here and remain neutralized.)
const EXTRA_TAGS: IWhiteList = {
	figure: [],
	figcaption: [],
	picture: [],
	source: ["src", "srcset", "type", "media", "sizes"],
	section: [],
	article: [],
	header: [],
	footer: [],
	main: [],
	aside: [],
	nav: [],
	details: [],
	summary: [],
	mark: [],
	small: [],
	time: ["datetime"],
	wbr: [],
	dl: [],
	dt: [],
	dd: []
};

// Broad-but-safe whitelist: xss defaults + extra semantic tags, with the global
// attributes allowed on every tag, plus media/link source attributes.
const buildWhiteList = (): IWhiteList => {
	const whiteList: IWhiteList = { ...getDefaultWhiteList(), ...EXTRA_TAGS };
	for (const tag of Object.keys(whiteList)) {
		const attrs = new Set(whiteList[tag] ?? []);
		GLOBAL_ATTRS.forEach((attr) => attrs.add(attr));
		whiteList[tag] = [...attrs];
	}
	whiteList.img = [
		...new Set([
			...(whiteList.img ?? []),
			"src",
			"srcset",
			"alt",
			"width",
			"height",
			"loading",
			"decoding"
		])
	];
	whiteList.a = [
		...new Set([...(whiteList.a ?? []), "href", "target", "rel", "name"])
	];
	return whiteList;
};

const defaultOptions: IFilterXSSOptions = {
	whiteList: buildWhiteList(),
	// Allow data-* / aria-* on any tag (value escaped — no scripting surface).
	onIgnoreTagAttr(_tag, name, value) {
		if (/^(data-|aria-)/i.test(name)) {
			return `${name}="${escapeAttrValue(value)}"`;
		}
		return undefined;
	}
};

/**
 * Sanitize an HTML string for safe rendering via `v-html`.
 *
 * Always run untrusted markup (CMS, user input, third-party APIs) through
 * this before binding to `v-html` — satisfies the `vue/no-v-html` ESLint
 * rule and the XSS rule in `AGENTS.md`.
 *
 * Uses `xss` (js-xss): a DOM-free, dependency-light sanitizer that runs
 * identically on the server and the client. That symmetry is deliberate —
 * both runtimes produce byte-identical output, so there is no SSR hydration
 * mismatch, and the markup is sanitized on the server too. (We avoid
 * DOM-based sanitizers like DOMPurify+jsdom: jsdom ships an ESM-only
 * transitive dep that breaks the CJS serverless bundle.)
 *
 * The default keeps a broad, CMS-friendly whitelist (text, links, images,
 * tables, figures, common semantic tags) with `class`/`id`/`style`/`data-*`/
 * `aria-*` preserved, while neutralizing scripts, event handlers,
 * `javascript:`/`vbscript:` URLs, CSS-based vectors, and embedding tags.
 *
 * `allowedTags` narrows the whitelist for contexts that shouldn't carry
 * block-level markup (short descriptions, teasers); each allowed tag then
 * permits only `allowedAttr` (default: href/target/rel).
 */
export const sanitizeHtml = (
	html: string | null | undefined,
	options?: { allowedTags?: string[]; allowedAttr?: string[] }
): string => {
	if (!html) return "";
	if (options?.allowedTags) {
		const attrs = options.allowedAttr ?? ["href", "target", "rel"];
		const whiteList: IWhiteList = Object.fromEntries(
			options.allowedTags.map((tag) => [tag, attrs])
		);
		return xss(html, { whiteList });
	}
	return xss(html, defaultOptions);
};

/**
 * Strip every HTML tag and collapse whitespace, optionally truncating to
 * a max length with an ellipsis. Use for plain-text contexts —
 * `useSeoMeta` description, schema.org `articleBody`, OG description,
 * char-count thresholds.
 *
 * Tag-stripping via regex keeps this dependency-free and identical on
 * server and client — the output is plain text, never rendered, so it
 * carries no XSS surface.
 *
 * Truncates on word boundary when possible so the last visible word
 * isn't cut mid-character.
 */
export const stripHtml = (
	html: string | null | undefined,
	maxLength?: number
): string => {
	if (!html) return "";
	const text = html
		// Only `<` that opens a tag, comment or doctype: "<500 g" is text.
		.replace(/<\/?[a-z!][^>]*>/gi, " ")
		.replace(/\s+/g, " ")
		.trim();
	return maxLength ? truncateText(text, maxLength) : text;
};

/**
 * Truncate plain text on a word boundary with an ellipsis — the cut
 * `stripHtml` applies, for text that is already tag-free.
 */
export const truncateText = (text: string, maxLength: number): string => {
	if (text.length <= maxLength) return text;
	const cut = text.slice(0, maxLength);
	const lastSpace = cut.lastIndexOf(" ");
	return (lastSpace > maxLength * 0.6 ? cut.slice(0, lastSpace) : cut) + "…";
};

// Case-sensitive, as in HTML (&Auml; vs &auml;). A Map, so a name such as
// `constructor` cannot resolve through the object prototype.
const NAMED_ENTITIES = new Map<string, string>(
	Object.entries({
		amp: "&",
		lt: "<",
		gt: ">",
		quot: '"',
		apos: "'",
		nbsp: " ",
		auml: "ä",
		Auml: "Ä",
		ouml: "ö",
		Ouml: "Ö",
		aring: "å",
		Aring: "Å",
		aelig: "æ",
		AElig: "Æ",
		oslash: "ø",
		Oslash: "Ø",
		uuml: "ü",
		Uuml: "Ü",
		eacute: "é",
		Eacute: "É",
		egrave: "è",
		Egrave: "È",
		agrave: "à",
		Agrave: "À",
		ndash: "–",
		mdash: "—",
		lsquo: "‘",
		rsquo: "’",
		ldquo: "“",
		rdquo: "”",
		laquo: "«",
		raquo: "»",
		hellip: "…",
		middot: "·",
		bull: "•",
		deg: "°",
		times: "×",
		plusmn: "±",
		frac12: "½",
		euro: "€",
		copy: "©",
		reg: "®",
		trade: "™",
		shy: ""
	})
);

/** Only these are also matched in any case (&AMP;, &NBSP;). */
const CASE_INSENSITIVE_ENTITIES = new Set([
	"amp",
	"lt",
	"gt",
	"quot",
	"apos",
	"nbsp"
]);

const namedEntity = (code: string): string | undefined => {
	const exact = NAMED_ENTITIES.get(code);
	if (exact !== undefined) return exact;
	const lower = code.toLowerCase();
	return CASE_INSENSITIVE_ENTITIES.has(lower)
		? NAMED_ENTITIES.get(lower)
		: undefined;
};

/**
 * Decode the entities catalogue HTML carries, for plain-text output that
 * is escaped again downstream (`useSeoMeta` would turn `&amp;` into
 * `&amp;amp;`). Unknown named entities are left as they are.
 */
export const decodeHtmlEntities = (text: string): string =>
	text.replace(
		/&(#x[0-9a-f]+|#\d+|[a-z][a-z0-9]*);/gi,
		(entity, code: string) => {
			if (code[0] !== "#") {
				return namedEntity(code) ?? entity;
			}
			const point =
				code[1] === "x" || code[1] === "X"
					? parseInt(code.slice(2), 16)
					: parseInt(code.slice(1), 10);
			return isDecodableCodePoint(point) ? String.fromCodePoint(point) : entity;
		}
	);

const MAX_CODE_POINT = 0x10ffff;
const SURROGATE_START = 0xd800;
const SURROGATE_END = 0xdfff;

/** `String.fromCodePoint` throws above U+10FFFF; NUL and lone surrogates are junk. */
const isDecodableCodePoint = (point: number): boolean =>
	Number.isInteger(point) &&
	point > 0 &&
	point <= MAX_CODE_POINT &&
	(point < SURROGATE_START || point > SURROGATE_END);
